import {
  formDialogBodyStyles, formDialogCancelStyles, formDialogCloseStyles, formDialogDangerStyles, formDialogFooterStyles,
  formDialogHeaderStyles, formDialogMarkStyles, formDialogPaperStyles, formDialogSubmitStyles, formDialogTitleStyles
} from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { brand, orange, red } from '@theme/primitives';
import { TriangleAlertIcon, XIcon } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

type ConfirmColor = 'primary' | 'warning' | 'error';

/** The mark's colour says how careful to be; the button is red only for what is not undone. */
const markColors: Record<ConfirmColor, string> = { primary: brand[300], warning: orange[300], error: red[300] };

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What the dialog asks about, as the header's mark. */
  icon?: ReactNode;
  confirmLabel: string;
  confirmColor?: ConfirmColor;
  /** Held off while what the dialog would confirm is not ready, such as a field left empty. */
  confirmDisabled?: boolean;
  onClose: () => void;
  /** `false` keeps the dialog open: what was confirmed did not go through. */
  onConfirm: () => Promise<boolean | void> | boolean | void;
  children: ReactNode;
}

/**
 * Asks before something that is not undone by a click, and says what it will do, in the look of
 * the record dialogs. What is said is the caller's; the dialog only waits for the answer and keeps
 * itself from being closed while it does.
 */
const ConfirmDialog = ({
  open, title, icon, confirmLabel, confirmColor = 'primary', confirmDisabled, onClose, onConfirm, children,
}: ConfirmDialogProps) => {
  const [isBusy, setBusy] = useState(false);
  const { t } = useLocalization();
  const titleId = useId();

  const confirm = async () => {
    setBusy(true);

    try {
      if (await onConfirm() !== false) onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: formDialogPaperStyles } }}
    >
      <Box sx={formDialogHeaderStyles}>
        {/* A question that warns has its mark even where the caller gives none: the button no longer carries the colour. */}
        {(icon || confirmColor !== 'primary') && (
          <Box sx={formDialogMarkStyles(markColors[confirmColor])}>{icon ?? <TriangleAlertIcon size={16} />}</Box>
        )}
        <Typography id={titleId} component="h2" sx={formDialogTitleStyles}>{title}</Typography>
        <IconButton aria-label={t('Common_Close')} onClick={onClose} disabled={isBusy} sx={formDialogCloseStyles}>
          <XIcon size={18} />
        </IconButton>
      </Box>
      <Box sx={bodyStyles}>{children}</Box>
      <Box sx={formDialogFooterStyles}>
        <Button onClick={onClose} disabled={isBusy} sx={formDialogCancelStyles}>
          {t('Common_Cancel')}
        </Button>
        <Button
          onClick={() => void confirm()}
          loading={isBusy}
          disabled={confirmDisabled}
          sx={confirmColor === 'error' ? formDialogDangerStyles : formDialogSubmitStyles}
        >
          {confirmLabel}
        </Button>
      </Box>
    </Dialog>
  );
};

// As much room under the text as over it, and a long list scrolls between the header and the footer.
const bodyStyles = {
  ...formDialogBodyStyles,
  pb: 2.5,
  overflowY: 'auto',
};

export default ConfirmDialog;
